import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, ChangeDetectorRef, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard } from '@memberjunction/ng-shared';
import { ResourceData } from '@memberjunction/core-entities';
import { RunView, Metadata, LogStatus } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import {
    marketingBlogEntity, marketingBlogStatusEntity, marketingContentCalendarEntity,
    marketingContentPlanEntity, marketingContentPlanItemEntity, marketingContentFeedEntity,
    marketingContentTemplateEntity, marketingContentStrategyEntity, marketingContentStrategyPillarEntity,
    marketingChannelConfigurationEntity, marketingContentTypeEntity, marketingContentStatusEntity,
    marketingNewsletterEntity, marketingSocialPostEntity, marketingBrandConfigurationEntity
} from 'mj_generatedentities';
import { FlyoutState, ContentStatus } from '../../types';

export type MarketingDashboardSection = 'overview' | 'content-library' | 'calendar' | 'approvals' | 'analytics' | 'social-queue' | 'settings';

interface SocialPlatformRow { ID: string; Name: string; }
interface IntegrationRow { ID: string; Name: string; Description: string; }
interface CompanyIntegrationRow { ID: string; CompanyID: string; IntegrationID: string; Integration: string; IsActive: boolean; AccessToken: string | null; APIKey: string | null; }
interface CompanyRow { ID: string; Name: string; }
interface MetricRow { ID: string; ContentTypeID: string; ContentID: string; Platform: string; MetricDate: string; PageViews: number | null; Likes: number | null; SocialShares: number | null; Comments: number | null; UniqueVisitors: number | null; }

@Component({
    standalone: false,
    selector: 'mkt-marketing-dashboard-v2',
    templateUrl: './marketing-dashboard-v2.component.html',
    styleUrls: ['./marketing-dashboard-v2.component.css']
})
@RegisterClass(BaseDashboard, 'MarketingDashboard')
export class MarketingDashboardV2Component extends BaseDashboard implements OnInit, OnDestroy {
    private cdr = inject(ChangeDetectorRef);

    // ─── Embedded Mode ───────────────────────────────
    /** When set, hides header/company selector and uses this company ID */
    @Input() companyId: string = '';
    /** True when embedded inside another dashboard (hides chrome) */
    get embedded(): boolean { return this.companyId !== ''; }

    // ─── Navigation ──────────────────────────────────
    activeSection: MarketingDashboardSection = 'overview';

    // ─── Company ─────────────────────────────────────
    defaultCompanyId = '';
    companies: CompanyRow[] = [];

    // ─── Lookup Data ─────────────────────────────────
    contentTypes: marketingContentTypeEntity[] = [];
    contentStatuses: marketingContentStatusEntity[] = [];
    blogStatuses: marketingBlogStatusEntity[] = [];
    socialPlatforms: SocialPlatformRow[] = [];
    integrations: IntegrationRow[] = [];

    // ─── Company-Scoped Data ─────────────────────────
    brandConfig: marketingBrandConfigurationEntity | null = null;
    channelConfigs: marketingChannelConfigurationEntity[] = [];
    strategy: marketingContentStrategyEntity | null = null;
    strategyPillars: marketingContentStrategyPillarEntity[] = [];
    contentPlan: marketingContentPlanEntity | null = null;
    contentPlanItems: marketingContentPlanItemEntity[] = [];
    calendarEntries: marketingContentCalendarEntity[] = [];
    contentFeeds: marketingContentFeedEntity[] = [];
    contentTemplates: marketingContentTemplateEntity[] = [];
    blogs: marketingBlogEntity[] = [];
    newsletters: marketingNewsletterEntity[] = [];
    socialPosts: marketingSocialPostEntity[] = [];
    companyIntegrations: CompanyIntegrationRow[] = [];
    metrics: MetricRow[] = [];

    // ─── Flyout ──────────────────────────────────────
    flyout: FlyoutState = { open: false, blogId: null, calendarEntryId: null, newsletterId: null, socialPostId: null };
    flyoutBlog: marketingBlogEntity | null = null;
    flyoutSocialPost: marketingSocialPostEntity | null = null;
    flyoutNewsletter: marketingNewsletterEntity | null = null;

    // ─── UI State ────────────────────────────────────
    isLoading = true;
    isPullingMetrics = false;
    saveStatus: Record<string, 'idle' | 'saving' | 'saved' | 'error'> = {};
    toasts: { message: string; type: 'success' | 'error' | 'info' | 'warning'; icon: string }[] = [];

    // ─── KPIs (computed) ─────────────────────────────
    get publishedThisWeek(): number {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return this.blogs.filter(b => {
            const pubDate = b.Get('PublishedDate');
            return pubDate && new Date(pubDate) >= weekAgo;
        }).length + this.socialPosts.filter(sp => {
            const pubDate = sp.Get('PublishedDate');
            return pubDate && new Date(pubDate) >= weekAgo;
        }).length;
    }

    get pendingApproval(): number {
        const draftStatusId = this.contentStatuses.find(s => s.Name === 'Draft')?.ID;
        if (!draftStatusId) return 0;
        return this.blogs.filter(b => b.Get('ContentStatusID') === draftStatusId).length
            + this.socialPosts.filter(sp => sp.Get('ContentStatusID') === draftStatusId).length
            + this.newsletters.filter(n => n.Get('ContentStatusID') === draftStatusId).length;
    }

    get activeIntegrations(): number {
        return this.companyIntegrations.filter(ci => ci.IsActive).length;
    }

    get topPerformer(): string {
        if (this.metrics.length === 0) return 'No data yet';
        const sorted = [...this.metrics].sort((a, b) => (b.PageViews ?? 0) - (a.PageViews ?? 0));
        const top = sorted[0];
        const blog = this.blogs.find(b => b.ID === top.ContentID);
        return blog ? blog.Get('Title') : `${top.Platform} content`;
    }

    // ─── BaseDashboard abstract implementations ─────
    protected override initDashboard(): void {
        this.NotifyLoadComplete();
    }

    async GetResourceDisplayName(_data: ResourceData): Promise<string> {
        return 'Marketing Dashboard';
    }

    async loadData(): Promise<void> {
        await this.loadAllData();
    }

    // ─── Lifecycle ───────────────────────────────────
    override async ngOnInit(): Promise<void> {
        if (this.companyId) {
            this.defaultCompanyId = this.companyId;
        }
        await super.ngOnInit();
        await this.loadAllData();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['companyId'] && !changes['companyId'].firstChange && changes['companyId'].currentValue) {
            this.defaultCompanyId = changes['companyId'].currentValue;
            await this.loadAllData();
        }
    }

    async onCompanyChange(): Promise<void> {
        await this.loadAllData();
    }

    setSection(section: MarketingDashboardSection): void {
        this.activeSection = section;
    }

    // ─── Data Loading ────────────────────────────────
    private async loadAllData(): Promise<void> {
        this.isLoading = true;
        this.cdr.detectChanges();

        try {
            const rv = new RunView();
            const cid = this.defaultCompanyId;

            const now = new Date();
            const weekAgo = new Date(now);
            weekAgo.setDate(weekAgo.getDate() - 7);
            const eightWeeksOut = new Date(now);
            eightWeeksOut.setDate(eightWeeksOut.getDate() + 56);
            const weekAgoStr = weekAgo.toISOString().split('T')[0];
            const eightWeeksStr = eightWeeksOut.toISOString().split('T')[0];
            const thirtyDaysAgo = new Date(now);
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];

            const queries: any[] = [
                /* 0  */ { EntityName: 'MJ: Companies', ResultType: 'simple', Fields: ['ID', 'Name'], MaxRows: 50 },
                /* 1  */ { EntityName: 'Content Types', ExtraFilter: 'IsActive=1', ResultType: 'entity_object' },
                /* 2  */ { EntityName: 'Content Status', ResultType: 'entity_object' },
                /* 3  */ { EntityName: 'Blog Status', ExtraFilter: 'IsActive=1', ResultType: 'entity_object' },
                /* 4  */ { EntityName: 'Social Platforms', ResultType: 'simple', Fields: ['ID', 'Name'] },
                /* 5  */ { EntityName: 'MJ: Integrations', ResultType: 'simple', Fields: ['ID', 'Name', 'Description'] },
            ];

            if (cid) {
                queries.push(
                    /* 6  */ { EntityName: 'Brand Configurations', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object', MaxRows: 1 },
                    /* 7  */ { EntityName: 'Channel Configurations', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'entity_object' },
                    /* 8  */ { EntityName: 'Content Strategies', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object', MaxRows: 1 },
                    /* 9  */ { EntityName: 'Content Strategy Pillars', ExtraFilter: `ContentStrategyID IN (SELECT ID FROM [marketing].[vwContentStrategies] WHERE CompanyID='${cid}' AND IsActive=1)`, OrderBy: 'Priority ASC', ResultType: 'entity_object' },
                    /* 10 */ { EntityName: 'Content Plans', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object', MaxRows: 1 },
                    /* 11 */ { EntityName: 'Content Plan Items', ExtraFilter: `ContentPlanID IN (SELECT ID FROM [marketing].[vwContentPlans] WHERE CompanyID='${cid}' AND IsActive=1) AND IsActive=1`, ResultType: 'entity_object' },
                    /* 12 */ { EntityName: 'Content Calendars', ExtraFilter: `CompanyID='${cid}' AND PlannedDate >= '${weekAgoStr}' AND PlannedDate <= '${eightWeeksStr}'`, OrderBy: 'PlannedDate ASC', ResultType: 'entity_object' },
                    /* 13 */ { EntityName: 'Content Feeds', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'entity_object' },
                    /* 14 */ { EntityName: 'Content Templates', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'entity_object' },
                    /* 15 */ { EntityName: 'Blogs', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 50 },
                    /* 16 */ { EntityName: 'Newsletters', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 20 },
                    /* 17 */ { EntityName: 'Social Posts', ExtraFilter: `CompanyID='${cid}'`, OrderBy: '__mj_CreatedAt DESC', ResultType: 'entity_object', MaxRows: 50 },
                    /* 18 */ { EntityName: 'MJ: Company Integrations', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'simple', Fields: ['ID', 'CompanyID', 'IntegrationID', 'Integration', 'IsActive', 'AccessToken', 'APIKey'] },
                    /* 19 */ { EntityName: 'Content Performance Metrics', ExtraFilter: `CompanyID='${cid}' AND MetricDate >= '${thirtyDaysAgoStr}'`, OrderBy: 'MetricDate DESC', ResultType: 'simple', Fields: ['ID', 'ContentTypeID', 'ContentID', 'Platform', 'MetricDate', 'PageViews', 'Likes', 'SocialShares', 'Comments', 'UniqueVisitors'], MaxRows: 500 },
                );
            }

            const results = await rv.RunViews(queries);

            // Lookups (always loaded)
            if (results[0].Success) {
                this.companies = results[0].Results as CompanyRow[];
                if (!this.defaultCompanyId && this.companies.length > 0) {
                    this.defaultCompanyId = this.companies[0].ID;
                    this.isLoading = false;
                    await this.loadAllData();
                    return;
                }
            }
            if (results[1].Success) this.contentTypes = results[1].Results as marketingContentTypeEntity[];
            if (results[2].Success) this.contentStatuses = results[2].Results as marketingContentStatusEntity[];
            if (results[3].Success) this.blogStatuses = results[3].Results as marketingBlogStatusEntity[];
            if (results[4].Success) this.socialPlatforms = results[4].Results as SocialPlatformRow[];
            if (results[5].Success) this.integrations = results[5].Results as IntegrationRow[];

            // Company-scoped
            if (cid && results.length > 6) {
                if (results[6].Success) this.brandConfig = (results[6].Results?.[0] as marketingBrandConfigurationEntity) ?? null;
                if (results[7].Success) this.channelConfigs = results[7].Results as marketingChannelConfigurationEntity[];
                if (results[8].Success) this.strategy = (results[8].Results?.[0] as marketingContentStrategyEntity) ?? null;
                if (results[9].Success) this.strategyPillars = results[9].Results as marketingContentStrategyPillarEntity[];
                if (results[10].Success) this.contentPlan = (results[10].Results?.[0] as marketingContentPlanEntity) ?? null;
                if (results[11].Success) this.contentPlanItems = results[11].Results as marketingContentPlanItemEntity[];
                if (results[12].Success) this.calendarEntries = results[12].Results as marketingContentCalendarEntity[];
                if (results[13].Success) this.contentFeeds = results[13].Results as marketingContentFeedEntity[];
                if (results[14].Success) this.contentTemplates = results[14].Results as marketingContentTemplateEntity[];
                if (results[15].Success) this.blogs = results[15].Results as marketingBlogEntity[];
                if (results[16]?.Success) this.newsletters = results[16].Results as marketingNewsletterEntity[];
                if (results[17]?.Success) this.socialPosts = results[17].Results as marketingSocialPostEntity[];
                if (results[18]?.Success) this.companyIntegrations = results[18].Results as CompanyIntegrationRow[];
                if (results[19]?.Success) this.metrics = results[19].Results as MetricRow[];
            }
        } catch (error) {
            console.error('MarketingDashboard: Error loading data', error);
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    // ─── Flyout Management ───────────────────────────
    openFlyout(type: 'blog' | 'social-post' | 'newsletter', id: string): void {
        this.flyout = {
            open: true,
            blogId: type === 'blog' ? id : null,
            socialPostId: type === 'social-post' ? id : null,
            newsletterId: type === 'newsletter' ? id : null,
            calendarEntryId: null,
        };
        if (type === 'blog') {
            this.flyoutBlog = this.blogs.find(b => b.ID === id) ?? null;
        } else if (type === 'social-post') {
            this.flyoutSocialPost = this.socialPosts.find(sp => sp.ID === id) ?? null;
        } else if (type === 'newsletter') {
            this.flyoutNewsletter = this.newsletters.find(n => n.ID === id) ?? null;
        }
        this.cdr.detectChanges();
    }

    closeFlyout(): void {
        this.flyout = { open: false, blogId: null, calendarEntryId: null, newsletterId: null, socialPostId: null };
        this.flyoutBlog = null;
        this.flyoutSocialPost = null;
        this.flyoutNewsletter = null;
        this.cdr.detectChanges();
    }

    // ─── Save Helpers ────────────────────────────────
    getSaveLabel(key: string): string {
        const s = this.saveStatus[key];
        if (s === 'saving') return 'Saving...';
        if (s === 'saved') return 'Saved!';
        if (s === 'error') return 'Error';
        return 'Save';
    }

    async onContentSaved(): Promise<void> {
        this.showToast('Content saved', 'success');
        await this.loadAllData();
    }

    async onStatusChange(event: { contentId: string; newStatus: string }): Promise<void> {
        const statusEntity = this.contentStatuses.find(s => s.Name === event.newStatus);
        if (!statusEntity) {
            this.showToast(`Status '${event.newStatus}' not found`, 'error');
            return;
        }

        let saved = false;
        const blog = this.blogs.find(b => b.ID === event.contentId);
        if (blog) {
            blog.Set('ContentStatusID', statusEntity.ID);
            saved = await blog.Save();
        }
        const socialPost = this.socialPosts.find(sp => sp.ID === event.contentId);
        if (socialPost) {
            socialPost.Set('ContentStatusID', statusEntity.ID);
            saved = await socialPost.Save();
        }
        const newsletter = this.newsletters.find(n => n.ID === event.contentId);
        if (newsletter) {
            newsletter.Set('ContentStatusID', statusEntity.ID);
            saved = await newsletter.Save();
        }

        if (saved) {
            this.showToast(`Status changed to ${event.newStatus}`, 'success');
        } else {
            this.showToast('Failed to update status', 'error');
        }
        await this.loadAllData();
    }

    // ─── Channel Config Operations ───────────────────
    async saveChannelConfig(config: marketingChannelConfigurationEntity): Promise<void> {
        this.saveStatus['channel-' + config.ID] = 'saving';
        this.cdr.detectChanges();
        const saved = await config.Save();
        this.saveStatus['channel-' + config.ID] = saved ? 'saved' : 'error';
        this.cdr.detectChanges();
        if (saved) {
            setTimeout(() => { this.saveStatus['channel-' + config.ID] = 'idle'; this.cdr.detectChanges(); }, 2000);
        }
    }

    async addChannelConfig(data: { contentTypeId: string; socialPlatformId: string | null; requiresApproval: boolean; autoPublish: boolean; maxPerWeek: number | null }): Promise<void> {
        const md = new Metadata();
        const entity = await md.GetEntityObject<marketingChannelConfigurationEntity>('Channel Configurations');
        entity.NewRecord();
        entity.Set('CompanyID', this.defaultCompanyId);
        entity.Set('ContentTypeID', data.contentTypeId);
        if (data.socialPlatformId) entity.Set('SocialPlatformID', data.socialPlatformId);
        entity.Set('RequiresApproval', data.requiresApproval);
        entity.Set('AutoPublish', data.autoPublish);
        if (data.maxPerWeek != null) entity.Set('MaxPerWeek', data.maxPerWeek);
        const saved = await entity.Save();
        if (saved) await this.loadAllData();
    }

    async deleteChannelConfig(configId: string): Promise<void> {
        const config = this.channelConfigs.find(c => c.ID === configId);
        if (!config) return;
        await config.Delete();
        await this.loadAllData();
    }

    // ─── Brand Config Operations ─────────────────────
    async saveBrandConfig(): Promise<void> {
        if (!this.brandConfig) return;
        this.saveStatus['brandConfig'] = 'saving';
        this.cdr.detectChanges();
        const saved = await this.brandConfig.Save();
        this.saveStatus['brandConfig'] = saved ? 'saved' : 'error';
        this.cdr.detectChanges();
        if (saved) {
            setTimeout(() => { this.saveStatus['brandConfig'] = 'idle'; this.cdr.detectChanges(); }, 2000);
        }
    }

    // ─── Strategy Operations ─────────────────────────
    async saveStrategy(): Promise<void> {
        if (!this.strategy) return;
        this.saveStatus['strategy'] = 'saving';
        this.cdr.detectChanges();
        const saved = await this.strategy.Save();
        this.saveStatus['strategy'] = saved ? 'saved' : 'error';
        this.cdr.detectChanges();
        if (saved) {
            setTimeout(() => { this.saveStatus['strategy'] = 'idle'; this.cdr.detectChanges(); }, 2000);
        }
    }

    async savePillar(pillar: marketingContentStrategyPillarEntity): Promise<void> {
        this.saveStatus['pillar-' + pillar.ID] = 'saving';
        this.cdr.detectChanges();
        const saved = await pillar.Save();
        this.saveStatus['pillar-' + pillar.ID] = saved ? 'saved' : 'error';
        this.cdr.detectChanges();
        if (saved) {
            setTimeout(() => { this.saveStatus['pillar-' + pillar.ID] = 'idle'; this.cdr.detectChanges(); }, 2000);
        }
    }

    // ─── Company Integration Operations ──────────────
    async addCompanyIntegration(data: { integrationId: string; accessToken?: string; apiKey?: string }): Promise<void> {
        const md = new Metadata();
        const entity = await md.GetEntityObject('MJ: Company Integrations');
        entity.NewRecord();
        entity.Set('CompanyID', this.defaultCompanyId);
        entity.Set('IntegrationID', data.integrationId);
        entity.Set('IsActive', true);
        entity.Set('IsExternalSystemReadOnly', false);
        entity.Set('ScheduleEnabled', false);
        entity.Set('ScheduleType', 'Manual');
        if (data.accessToken) entity.Set('AccessToken', data.accessToken);
        if (data.apiKey) entity.Set('APIKey', data.apiKey);
        const saved = await entity.Save();
        if (saved) await this.loadAllData();
    }

    async toggleCompanyIntegration(ciId: string): Promise<void> {
        const ci = this.companyIntegrations.find(c => c.ID === ciId);
        if (!ci) return;
        const rv = new RunView();
        const result = await rv.RunView({
            EntityName: 'MJ: Company Integrations',
            ExtraFilter: `ID='${ciId}'`,
            ResultType: 'entity_object',
        });
        if (!result.Success || result.Results.length === 0) return;
        const entity = result.Results[0];
        entity.Set('IsActive', !ci.IsActive);
        await entity.Save();
        await this.loadAllData();
    }

    async deleteCompanyIntegration(ciId: string): Promise<void> {
        const rv = new RunView();
        const result = await rv.RunView({
            EntityName: 'MJ: Company Integrations',
            ExtraFilter: `ID='${ciId}'`,
            ResultType: 'entity_object',
        });
        if (!result.Success || result.Results.length === 0) return;
        await result.Results[0].Delete();
        await this.loadAllData();
    }

    // ─── Content Feed Operations ─────────────────────
    async saveContentFeed(feed: marketingContentFeedEntity): Promise<void> {
        this.saveStatus['feed-' + feed.ID] = 'saving';
        this.cdr.detectChanges();
        const saved = await feed.Save();
        this.saveStatus['feed-' + feed.ID] = saved ? 'saved' : 'error';
        this.cdr.detectChanges();
        if (saved) {
            setTimeout(() => { this.saveStatus['feed-' + feed.ID] = 'idle'; this.cdr.detectChanges(); }, 2000);
        }
    }

    // ─── Toast Notifications ─────────────────────────
    showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info'): void {
        const icons: Record<string, string> = { success: 'fa-check-circle', error: 'fa-exclamation-circle', info: 'fa-info-circle', warning: 'fa-exclamation-triangle' };
        this.toasts.push({ message, type, icon: icons[type] });
        this.cdr.detectChanges();
        setTimeout(() => { this.toasts.shift(); this.cdr.detectChanges(); }, 4000);
    }

    // ─── Blog Actions ────────────────────────────────
    async onDeleteBlog(blogId: string): Promise<void> {
        const blog = this.blogs.find(b => b.ID === blogId);
        if (!blog) {
            console.error('[onDeleteBlog] Blog not found in local state:', blogId);
            return;
        }
        console.log('[onDeleteBlog] Deleting blog', blogId, blog.Title);
        try {
            const ok = await blog.Delete();
            if (!ok) {
                const msg = blog.LatestResult?.Message || 'Delete returned false with no error message';
                console.error('[onDeleteBlog] Delete failed:', msg, blog.LatestResult);
                this.showToast(`Failed to delete blog: ${msg}`, 'error');
                return;
            }
            console.log('[onDeleteBlog] Delete succeeded');
            this.showToast('Blog deleted', 'success');
            this.closeFlyout();
            await this.loadAllData();
        } catch (error) {
            console.error('[onDeleteBlog] Delete threw:', error);
            this.showToast(`Failed to delete blog: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        }
    }

    // ─── Social Post Actions ─────────────────────────
    async onDeleteSocialPost(postId: string): Promise<void> {
        const post = this.socialPosts.find(sp => sp.ID === postId);
        if (!post) {
            console.error('[onDeleteSocialPost] Post not found:', postId);
            return;
        }
        try {
            const ok = await post.Delete();
            if (!ok) {
                const msg = post.LatestResult?.Message || 'Delete returned false with no error message';
                console.error('[onDeleteSocialPost] Delete failed:', msg, post.LatestResult);
                this.showToast(`Failed to delete post: ${msg}`, 'error');
                return;
            }
            this.showToast('Social post deleted', 'success');
            this.closeFlyout();
            await this.loadAllData();
        } catch (error) {
            console.error('[onDeleteSocialPost] Delete threw:', error);
            this.showToast(`Failed to delete post: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        }
    }

    async onPublishSocialPost(postId: string): Promise<void> {
        this.showToast('Publishing social post...', 'info');
        try {
            const rv = new RunView();
            const actionResult = await rv.RunView<{ ID: string }>({
                EntityName: 'MJ: Actions',
                ExtraFilter: `Name='Publish Social Post'`,
                ResultType: 'simple',
                Fields: ['ID'],
                MaxRows: 1,
            });

            if (!actionResult.Success || actionResult.Results.length === 0) {
                this.showToast('Publish Social Post action not found. Is the Marketing package loaded?', 'error');
                return;
            }

            const gql = `mutation RunAction($input: RunActionInput!) { RunAction(input: $input) { Success Message ResultCode } }`;
            const result = await GraphQLDataProvider.ExecuteGQL(gql, {
                input: {
                    ActionID: actionResult.Results[0].ID,
                    Params: [
                        { Name: 'SocialPostID', Value: postId, Type: 'Input' },
                        { Name: 'CompanyID', Value: this.defaultCompanyId, Type: 'Input' },
                    ],
                },
            });

            const response = result?.RunAction;
            if (response?.Success) {
                this.showToast('Published successfully!', 'success');
                await this.loadAllData();
            } else {
                this.showToast(`Publish failed: ${response?.Message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            this.showToast(`Publish error: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        }
    }

    async onScheduleSocialPost(postId: string): Promise<void> {
        const post = this.socialPosts.find(sp => sp.ID === postId);
        if (!post) return;
        const scheduledDate = post.Get('ScheduledPublishDate');
        if (!scheduledDate) {
            this.showToast('Set a scheduled date first', 'warning');
            return;
        }
        // Mark as scheduled by setting status to Approved (scheduling handled by the scheduled job system)
        const approvedStatus = this.contentStatuses.find(s => s.Name === 'Approved');
        if (approvedStatus) {
            post.Set('ContentStatusID', approvedStatus.ID);
            const saved = await post.Save();
            if (saved) {
                this.showToast(`Scheduled for ${new Date(scheduledDate).toLocaleDateString()}`, 'success');
                await this.loadAllData();
            } else {
                this.showToast('Failed to schedule post', 'error');
            }
        }
    }

    // ─── Analytics Pull ────────────────────────────────
    async onPullMetrics(): Promise<void> {
        if (this.isPullingMetrics || !this.defaultCompanyId) return;
        this.isPullingMetrics = true;
        this.showToast('Pulling metrics from all connected analytics integrations...', 'info');
        this.cdr.detectChanges();

        try {
            const rv = new RunView();
            const actionResult = await rv.RunView<{ ID: string }>({
                EntityName: 'MJ: Actions',
                ExtraFilter: `Name='Pull Performance Metrics'`,
                ResultType: 'simple',
                Fields: ['ID'],
                MaxRows: 1,
            });

            if (!actionResult.Success || actionResult.Results.length === 0) {
                this.showToast('Pull Performance Metrics action not found. Is the Marketing package loaded?', 'error');
                return;
            }

            const gql = `mutation RunAction($input: RunActionInput!) { RunAction(input: $input) { Success Message ResultCode } }`;
            const result = await GraphQLDataProvider.ExecuteGQL(gql, {
                input: {
                    ActionID: actionResult.Results[0].ID,
                    Params: [
                        { Name: 'CompanyID', Value: this.defaultCompanyId, Type: 'Input' },
                    ],
                },
            });

            const response = result?.RunAction;
            if (response?.Success) {
                const prevCount = this.metrics.length;
                await this.loadAllData();
                const newCount = this.metrics.length;
                if (newCount > prevCount) {
                    this.showToast(`Pulled ${newCount - prevCount} new metric(s)`, 'success');
                } else if (newCount === 0) {
                    this.showToast('No metrics available. The HubSpot PAT may need the cms-analytics-api-access scope, or no analytics integrations are configured.', 'warning');
                } else {
                    this.showToast(`Metrics up to date (${newCount} records)`, 'info');
                }
            } else {
                this.showToast(`Pull failed: ${response?.Message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            this.showToast(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
        } finally {
            this.isPullingMetrics = false;
            this.cdr.detectChanges();
        }
    }

    // ─── Utility ─────────────────────────────────────
    getStatusName(statusId: string): string {
        return this.contentStatuses.find(s => s.ID === statusId)?.Name ?? 'Unknown';
    }

    getContentTypeName(typeId: string): string {
        return this.contentTypes.find(t => t.ID === typeId)?.Name ?? 'Unknown';
    }

    getPlatformName(platformId: string): string {
        return this.socialPlatforms.find(p => p.ID === platformId)?.Name ?? '';
    }

    getIntegrationName(integrationId: string): string {
        return this.integrations.find(i => i.ID === integrationId)?.Name ?? 'Unknown';
    }

    getMetricsForContent(contentId: string): MetricRow[] {
        return this.metrics.filter(m => m.ContentID === contentId);
    }
}

export function LoadMarketingDashboard(): void { /* anti-tree-shake */ }
